import { useState, useEffect } from 'react';
import axios from 'axios';
import { ShoppingBag, Loader2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHOPIFY_STORE_URL = 'https://darkar.co';

export const ProductsSection = ({ 
  title = "Tienda", 
  limit = 4,
  showViewAll = true,
  compact = true 
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, [limit]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      // Fetch products and custom order in parallel
      const [productsRes, orderRes] = await Promise.all([
        axios.get(`${API}/shopify/products?limit=${limit * 2}`),
        axios.get(`${API}/shop-product-order`).catch(() => ({ data: [] }))
      ]);
      
      const shopifyProducts = productsRes.data.products;
      const customOrder = orderRes.data;
      
      // Create order map
      const orderMap = {};
      customOrder.forEach(item => {
        orderMap[item.handle] = item.order;
      });
      
      // Sort products by custom order, then by availability
      const sortedProducts = [...shopifyProducts].sort((a, b) => {
        const orderA = orderMap[a.handle] ?? 999;
        const orderB = orderMap[b.handle] ?? 999;
        
        if (orderA !== orderB) return orderA - orderB;
        
        // If same order, sort by availability
        const aAllSoldOut = a.variants?.every(v => v.available === false) ?? false;
        const bAllSoldOut = b.variants?.every(v => v.available === false) ?? false;
        
        if (aAllSoldOut && !bAllSoldOut) return 1;
        if (!aAllSoldOut && bAllSoldOut) return -1;
        return 0;
      });
      
      setProducts(sortedProducts.slice(0, limit));
      setError(null);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('No se pudieron cargar los productos');
    } finally {
      setLoading(false);
    }
  };

  // Check if product is sold out
  const isProductSoldOut = (product) => {
    return product.variants?.every(v => v.available === false) ?? false;
  };

  if (loading) {
    return (
      <div className="py-12">
        <div className="flex items-center justify-center">
          <Loader2 className="animate-spin text-[#E91E8C]" size={32} />
        </div>
      </div>
    );
  }

  if (error || products.length === 0) {
    return null; // Don't show section if no products
  }

  return (
    <section className="py-0" data-testid="products-section">
      <div>
        {/* Header - Badge style like Shows */}
        <div className="flex items-center justify-center mb-10">
          <Link 
            to="/tienda"
            className="section-title w-full text-center px-8 py-3 bg-black text-white text-2xl md:text-3xl uppercase tracking-wide hand-drawn-subtle hover:bg-[#E91E8C] transition-colors"
            style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
            data-testid="view-all-products"
          >
            Tienda
          </Link>
        </div>

        {/* Products Grid */}
        <div className={`grid gap-4 md:gap-6 ${
          compact 
            ? 'grid-cols-2 md:grid-cols-4' 
            : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
        }`}>
          {products.map((product) => {
            const soldOut = isProductSoldOut(product);
            const productUrl = `${SHOPIFY_STORE_URL}/products/${product.handle}`;
            const price = product.variants?.[0]?.price;
            
            return (
              <a
                key={product.id}
                href={productUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`group block bg-black hand-drawn-subtle overflow-hidden transition-transform hover:scale-[1.02] ${
                  soldOut ? 'grayscale opacity-70' : ''
                }`}
                style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                data-testid={`product-card-${product.handle}`}
              >
                {/* Product Image */}
                <div className="relative aspect-square overflow-hidden bg-white">
                  {(product.images?.[0]?.src || product.images?.[0]?.url) ? (
                    <img
                      src={product.images[0].src || product.images[0].url}
                      alt={product.title}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-800">
                      <ShoppingBag size={48} className="text-white/20" />
                    </div>
                  )}
                  
                  {/* Sold Out Badge */}
                  {soldOut && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                      AGOTADO
                    </div>
                  )}
                  
                  {/* External Link Icon on Hover */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="bg-[#E91E8C] p-3 rounded-full">
                      <ExternalLink size={24} className="text-white" />
                    </div>
                  </div>
                </div>
                
                {/* Product Info */}
                <div className="p-3">
                  <h3 className="text-white font-bold text-sm line-clamp-2 group-hover:text-[#E91E8C] transition-colors">
                    {product.title}
                  </h3>
                  
                  {product.description && (
                    <p className="text-white/50 text-xs mt-1 line-clamp-2">
                      {product.description}
                    </p>
                  )}
                  
                  {price && (
                    <p className="text-[#E91E8C] font-bold text-base mt-1">
                      ${parseFloat(price).toFixed(2)}
                    </p>
                  )}
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};
